/** 工程看板汇总项目进度、团队统计和真实市检日历。
 * 搜索置于列表筛选栏首位，团队/阶段/状态支持多选；日历只跟随团队范围，避免列表条件隐藏预约。
 * 筛选仅收敛已有可见项目，操作权限继续沿用项目与服务端校验。
 * 首屏空间优化：项目摘要和操作合并进现有面包屑插槽，正文直接展示周历；手机保留可换行工具栏。
 */
import React, { useMemo, useState, useEffect, useCallback, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../context/AppContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PHASES, TEAMS, getPhase, COMPLETED_INDEX } from "../data/constants";
import { assessRisk, phaseTiming, fmtDate } from "../lib/schedule";
import CreateProject from "../components/CreateProject";
import { PullToRefresh } from "../components/PullToRefresh";
import WeeklyInspectionCalendar from "../components/WeeklyInspectionCalendar";
import { KickoffCountdownCards } from "../components/KickoffCountdownCards";
import { DeptBriefCard } from "../components/DeptBriefCard";
import BreadcrumbPopButton from "../components/BreadcrumbPopButton";
import { BreadcrumbSlotContext } from "../context/BreadcrumbSlot";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tooltip as InfoTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import BoardMultiSelect from "../components/BoardMultiSelect";
import { Search, Plus, CheckCircle2, Pencil, FileDown, Bell, Loader2, Hammer, Building2, HardHat, MessageSquare, ChevronDown, ChevronUp, LayoutDashboard, AlertTriangle } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell, LabelList } from "recharts";
import * as XLSX from "xlsx";
import { fmtD } from "../lib/date";
import { SiteCostChip, SiteCostDetailRow } from "../components/SiteCostChip";
import { useStaffOptions } from "../components/StaffPicker";
import { ReassignBanner } from "../components/ReassignBanner";
import { defaultTeamFilter, teamFilterKey, canOperateProject, isTeamManager, userTeams, inUserTeam } from "../lib/teamScope";
import { isBoardReturn, consumeBoardReturn, markBoardReturn, restoreBoardFilter, clearBoardFilterStorage, normalizeBoardSelection } from "../lib/boardFilters";
import { MobileProjectCard, useMobileWorkflow } from "../components/mobile/MobileWorkflowCards";
import { useEngineeringProjects } from "../hooks/useEngineeringProjects";

const TEAM_BRANCH = { team_norcal: "norcal", team_1: "la", team_2: "la", team_3: "la", team_sd: "sd" };
const effBranch = (p) => p.branch || TEAM_BRANCH[p.team] || "";
const LIST_PAGE = 20;         // 工程项目列表首屏 / 每批渲染行数
const LIST_PREFETCH_PX = 200; // 距底部多少像素开始渲染下一批

const STATUS_META = {
  over: { zh: "超期", en: "Overdue", color: "#e11b22", bg: "#fee2e2" },
  warn: { zh: "预警", en: "Warning", color: "#d97706", bg: "#fef3c7" },
  normal: { zh: "正常", en: "On Track", color: "#16a34a", bg: "#dcfce7" },
};

const TEAM_FILTER_VALUES = TEAMS.map((team) => team.id);
const PHASE_FILTER_VALUES = PHASES.map((_, index) => String(index));
const STATUS_FILTER_VALUES = Object.keys(STATUS_META);

/** 将看板单选升级为多选，同时保留统计卡、URL 的单值快捷入口。
 * name/user/fallback/keep 沿用详情返回缓存规则；allowedValues 校验可选范围。
 * 返回数组状态与兼容字符串/数组的 setter；相同条件复用引用，避免刷新数据时重置列表位置。
 */
function useBoardMultiFilter(name, user, fallback, keep, allowedValues) {
  const [values, setValues] = useState(() => normalizeBoardSelection(restoreBoardFilter(name, user, fallback, keep), allowedValues));
  const setFilter = useCallback((next) => setValues((previous) => {
    const normalized = normalizeBoardSelection(typeof next === "function" ? next(previous) : next, allowedValues);
    return normalized.length === previous.length && normalized.every((value, index) => value === previous[index]) ? previous : normalized;
  }), [allowedValues]);
  return [values, setFilter];
}

function FilterChip({ testid, label, onClear }) {
  return (
    <span data-testid={testid} className="inline-flex max-w-full items-center gap-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-full pl-2.5 pr-1 py-0.5">
      {/* 多选条件可能较长，允许标签换行，避免将工具栏撑出页面。 */}
      <span className="min-w-0 break-words">{label}</span>
      <button data-testid={`${testid}-clear`} onClick={onClear} aria-label={`✕ ${label}`} className="ml-0.5 h-4 w-4 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-red-200/60 leading-none">✕</button>
    </span>
  );
}

// 设计移交项目的开工倒计时：开工日 ≤7 天（琥珀）/ 已过开工日仍在 1-Pre-construction（红）
export function startCountdown(p) {
  const fromDesign = p.designProjectId || p.designHandoffAt || p.createdFrom === "design_handoff";
  const start = p.startDate || p.expectedStart;
  if (!fromDesign || !start) return null;
  const d = new Date(`${String(start).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days >= 0 && days <= 7) return { days, overdue: false };
  if (days < 0 && p.phaseIndex === 0) return { days: -days, overdue: true };
  return null;
}

function StartCountdownChip({ p, lang }) {
  const c = startCountdown(p);
  if (!c) return null;
  const zh = lang === "zh";
  const label = c.overdue ? (zh ? `开工日已过 ${c.days} 天` : `Start ${c.days}d overdue`) : c.days === 0 ? (zh ? "今日开工" : "Starts today") : (zh ? `${c.days} 天后开工` : `Starts in ${c.days}d`);
  return (
    <span data-testid={`start-countdown-${p.id}`} data-days={c.overdue ? -c.days : c.days} title={`${zh ? "计划开工" : "Planned start"} ${fmtD(p.startDate || p.expectedStart)}`}
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 shrink-0 border ${c.overdue ? "text-red-700 bg-red-50 border-red-200" : "text-amber-800 bg-amber-100 border-amber-300"}`}>
      <Hammer className="h-3 w-3" />{label}
    </span>
  );
}

// 7-step phase progress indicator
function StepProgress({ current }) {
  return (
    <div className="flex items-center gap-0.5">
      {PHASES.map((ph, i) => {
        const done = i < current;
        const active = i === current;
        const bg = active ? "#2563eb" : done ? "#16a34a" : "#e2e8f0";
        const fg = active || done ? "#fff" : "#94a3b8";
        return (
          <React.Fragment key={ph.key}>
            <span className="inline-flex items-center justify-center rounded-full text-[9px] font-bold" style={{ width: 18, height: 18, background: bg, color: fg }} title={ph.zh}>{i + 1}</span>
            {i < PHASES.length - 1 && <span className="h-0.5 w-1.5" style={{ background: i < current ? "#16a34a" : "#e2e8f0" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function TeamBoard() {
  const { t, lang, user, allProjects, advancePhase, api, refresh, refreshProjects , permOf } = useApp();
  // 暂停项目此前仍占据工程清单、阶段统计和导出。统一先筛选工程展示数据，
  // 下游桌面/手机、数量、筛选和导出使用同一口径；不改全局数据或项目权限。
  const visibleProjects = useEngineeringProjects(allProjects, refreshProjects);
  const mobile = useMobileWorkflow();
  // 一次性「返回令牌」：本次挂载是否为「从项目详情返回」——是则恢复筛选，否则清空缓存回默认。
  // 注意：必须用挂载时定值的 keepFilters（而非在 effect 里重新读），否则 StrictMode 二次执行会误判成「不保留」。
  const keepFilters = useMemo(() => isBoardReturn(), []);
  useEffect(() => {
    if (!keepFilters) clearBoardFilterStorage(user);
    consumeBoardReturn();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const [salesFilter, setSalesFilter] = useState(() => restoreBoardFilter("sales", user, "all", keepFilters));
  const [query, setQuery] = useState(() => restoreBoardFilter("query", user, "", keepFilters));
  const [teamFilter, setTeamFilter] = useBoardMultiFilter("team", user, defaultTeamFilter(user), keepFilters, TEAM_FILTER_VALUES);
  const [phaseFilter, setPhaseFilter] = useBoardMultiFilter("phase", user, "all", keepFilters, PHASE_FILTER_VALUES);
  const [statusFilter, setStatusFilter] = useBoardMultiFilter("status", user, "all", keepFilters, STATUS_FILTER_VALUES);
  const [branchFilter, setBranchFilter] = useState(() => (isTeamManager(user) ? restoreBoardFilter("branch", user, "all", keepFilters) : "all"));
  const [riskFilter, setRiskFilter] = useState(() => restoreBoardFilter("risk", user, "all", keepFilters));
  useEffect(() => { localStorage.setItem("tb_branchFilter", branchFilter); }, [branchFilter]);
  // 【需求·层级去重·配套】一旦团队切到具体分组，就清掉可能残留的分公司筛选：
  // 让「隐藏的筛选项不在后台偷偷生效」，也避免分公司统计卡高亮态与列表结果对不上。
  // 只依赖 teamFilter：切回「全部团队」时保留用户新选的分公司（不会被这次清理波及）。
  useEffect(() => { if (teamFilter.length && branchFilter !== "all") setBranchFilter("all"); }, [teamFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const [branchGaps, setBranchGaps] = useState(null);
  // 二-4：digestBusy / runBranchDigest 已随「立即推送分公司周报」按钮一并移除，见下方 branch-tools 注释。
  const missingBranch = useMemo(() => visibleProjects.filter((p) => !p.branch).length, [visibleProjects]);
  const backfillBranch = async () => {
    if (!window.confirm(lang === "zh" ? `将 ${missingBranch} 个未设分公司的项目按施工组写入所属分公司（LA 一/二/三组 → 洛杉矶，NorCal → 北加州，SD → 圣地亚哥）？之后可在「编辑项目信息」单独调整。` : `Backfill branch for ${missingBranch} project(s) from their crew? You can adjust individually later.`)) return;
    try {
      const { data } = await api.post("/hrms/projects/backfill-branch", {});
      setBranchGaps(data);
      toast.success(lang === "zh" ? `已补齐 ${data.updated} 个项目${data.skipped ? `，${data.skipped} 个无施工组跳过` : ""}` : `Backfilled ${data.updated} project(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || (lang === "zh" ? "补齐失败" : "Backfill failed")); }
  };
  // 二-4：原 runBranchDigest（立即推送分公司周报）已删除，见上方 digestBusy 注释。
  const staff = useStaffOptions();
  const branchList = useMemo(() => staff.branches || [], [staff.branches]);
  const branchLabel = (id) => { const b = branchList.find((x) => x.id === id); return b ? (lang === "zh" ? b.zh : b.en) : (id || (lang === "zh" ? "未设置" : "Unset")); };
  useEffect(() => { localStorage.setItem("tb_query", query); }, [query]);
  // JSON 数组完整保存多选项；恢复时仍兼容旧版单值缓存。
  useEffect(() => { localStorage.setItem(teamFilterKey("tb", user), JSON.stringify(teamFilter)); }, [teamFilter, user]);
  useEffect(() => { localStorage.setItem("tb_phaseFilter", JSON.stringify(phaseFilter)); }, [phaseFilter]);
  useEffect(() => { localStorage.setItem("tb_statusFilter", JSON.stringify(statusFilter)); }, [statusFilter]);
  // 补齐其余 3 个筛选器的缓存（原先只在内存里，返回详情页时会丢）
  useEffect(() => { localStorage.setItem("tb_salesFilter", salesFilter); }, [salesFilter]);
  useEffect(() => { localStorage.setItem("tb_riskFilter", riskFilter); }, [riskFilter]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [sp, setSp] = useSearchParams();
  const clearUrlFilter = (key) => {
    const next = new URLSearchParams(sp);
    next.delete(key);
    setSp(next, { replace: true });
  };
  // 【需求·层级去重·URL 入口】从「分公司周报」等外部入口跳回看板时 URL 会带 ?branch=xxx，
  // 此时强制回到「全部团队」视角：否则团队（⊂分公司）与分公司两个条件叠加会互相排斥、把结果筛空。
  const branchParam = sp.get("branch");
  // 只在分公司入口变化时重置团队，避免清除 URL 状态参数时冲掉刚选好的多个团队。
  useEffect(() => { if (branchParam) { setBranchFilter(branchParam); setTeamFilter("all"); } }, [branchParam, setTeamFilter]);
  const nav = useNavigate();
  // 项目摘要、导出、创建和工程总览共用 Layout 的面包屑插槽，避免再占一条独立吸顶栏。
  // 与「项目财务」页同一机制（见 components/BreadcrumbPopButton.jsx / context/BreadcrumbSlot.js）。
  const breadcrumbSlotRef = useContext(BreadcrumbSlotContext);
  const [bcSlot, setBcSlot] = useState(null);
  useEffect(() => { setBcSlot(breadcrumbSlotRef?.current || null); }, [breadcrumbSlotRef]);
  // 看板 → 项目详情的统一出口：先埋「返回令牌」，返回时才能恢复主搜索条件
  const goProject = useCallback((id) => {
    markBoardReturn(`/project/${id}`);
    nav(`/project/${id}`);
  }, [nav]);
  useEffect(() => {
    const oid = sp.get("open");
    if (oid) {
      nav(`/project/${oid}`, { replace: true });
      return;
    }
    const risk = sp.get("risk");
    const st = sp.get("status");
    if (["high", "low"].includes(risk)) { setStatusFilter("all"); setRiskFilter(risk); }
    else if (["over", "warn", "normal"].includes(st)) { setRiskFilter("all"); setStatusFilter(st); }
    else if (!keepFilters) setRiskFilter("all");   // 仅「非返回」场景回落到默认，避免覆盖恢复值
    if (sp.get("new") === "1" && canCreateProject) { setShowCreate(true); nav("/board", { replace: true }); }
  // URL 只在入口条件变化时应用，避免项目刷新把用户新勾选的多个状态覆盖成 URL 的单状态。
  }, [sp, nav, user.role, keepFilters, setStatusFilter]);

  const canManageAll = ["admin", "ceo"].includes(user.role) || user.role === "eng_manager";
  // 【2026-10-25】「新建项目」按钮独立走动作权限点 /board::create（与 POST /projects 接口同一份判定，
  // 矩阵/账号配置授予或屏蔽后按钮与接口同步生效）；快照未覆盖时退回原角色判断，行为不变。
  // 注意 canManageAll 还承担团队筛选标签/只读标记等展示语义，保持原口径不动。
  const canCreateProject = permOf("/board::create") ?? (["admin", "ceo"].includes(user.role) || user.role === "eng_manager");
  const isSales = user.role === "sales";
  const isAftersales = user.role === "aftersales" || user.role === "branch_sales_manager";   // 分公司范围只读视图（售后 / 分公司销售部经理）
  const canOperate = canManageAll || user.role === "member";
  const canOperateRow = (p) => canOperate && canOperateProject(user, p);
  // 销售「我的客户」视图：施工日志未读角标（原「施工项目(销售)」页并入此处）
  const [unreadMap, setUnreadMap] = useState({});
  const markReadInFlightRef = useRef(new Set());
  const loadUnread = useCallback(() => {
    if (!isSales) return;
    api.get("/my-construction-projects").then(({ data }) => setUnreadMap(Object.fromEntries((data.projects || []).map((p) => [p.id, { unread: p.unread || 0, noteCount: p.noteCount || 0 }])))).catch(() => {});
  }, [api, isSales]);
  useEffect(() => { loadUnread(); }, [loadUnread]);
  const totalUnread = useMemo(() => Object.values(unreadMap).reduce((s, x) => s + (x.unread || 0), 0), [unreadMap]);
  const openProject = (p) => {
    const shouldMarkRead = isSales && unreadMap[p.id]?.unread > 0;
    if (shouldMarkRead && markReadInFlightRef.current.has(p.id)) return;
    goProject(p.id);
    if (!shouldMarkRead) return;

    markReadInFlightRef.current.add(p.id);
    let markReadRequest;
    try { markReadRequest = api.post(`/projects/${p.id}/notes/mark-all-read`); }
    catch (e) { markReadRequest = Promise.reject(e); }
    Promise.resolve(markReadRequest)
      .then(() => window.dispatchEvent(new Event("construction-unread-changed")))
      .catch((e) => toast.error(e?.response?.data?.detail || e?.message || (lang === "zh"
        ? "项目已打开，但施工日志标记已读失败，请重试"
        : "Project opened, but construction notes could not be marked read. Please retry.")))
      .finally(() => markReadInFlightRef.current.delete(p.id));
  };
  const [urged, setUrged] = useState([]);
  const [pendingActionById, setPendingActionById] = useState({});
  const projectActionLocksRef = useRef(new Set());
  const beginProjectAction = useCallback((id, action) => {
    if (projectActionLocksRef.current.has(id)) return false;
    projectActionLocksRef.current.add(id);
    setPendingActionById((previous) => ({ ...previous, [id]: action }));
    return true;
  }, []);
  const finishProjectAction = useCallback((id) => {
    projectActionLocksRef.current.delete(id);
    setPendingActionById((previous) => {
      if (!previous[id]) return previous;
      const next = { ...previous };
      delete next[id];
      return next;
    });
  }, []);
  // 二-6：missingArchive / backfilling / backfillReceipt / archiveOpen 状态及 loadMissing / missingIds
  // 已随「缺少签约归档」提醒横幅一并移除，见下方 UI 注释。
  const [surveyOverdue, setSurveyOverdue] = useState({});
  useEffect(() => {
    api.get("/site-survey/overdue-risks").then(({ data }) => setSurveyOverdue(Object.fromEntries((data.items || []).map((x) => [x.projectId, x])))).catch(() => {});
  }, [api]);
  const [siteCosts, setSiteCosts] = useState({});
  const [costOpen, setCostOpen] = useState(null);
  useEffect(() => {
    api.get("/site-orders/costs-summary").then(({ data }) => setSiteCosts(Object.fromEntries((data.items || []).map((x) => [x.projectId, x])))).catch(() => {});
  }, [api]);

  // 二-6：原 runBackfill（一键补档）与 exportMissing（导出缺归档清单）已随提醒横幅删除，
  // 见上方 missingArchive 状态注释。

  const urge = async (p, timing) => {
    if (!beginProjectAction(p.id, "urge")) return;
    const phase = getPhase(p.phaseIndex);
    const msg = `阶段整改催办 · ${p.address}：当前【${phase.zh}】已停留 ${timing?.stayed ?? 0} 天(标准 ${timing?.standard ?? "-"} 天，超期 ${timing?.deviation ?? 0} 天)，请负责人 ${p.manager} / ${p.teamName} 尽快跟进整改。`;
    try {
      await api.post(`/projects/${p.id}/urge`, { message: msg });
      setUrged((u) => u.includes(p.id) ? u : [...u, p.id]);
      toast.success(lang === "zh" ? `已向 ${p.manager} / ${p.teamName} 发送催办` : `Urge sent to ${p.manager} / ${p.teamName}`);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || (lang === "zh" ? "催办失败，请重试" : "Could not send reminder. Please retry.")); }
    finally { finishProjectAction(p.id); }
  };

  const baseRows = useMemo(() => {
    // 工程工作清单与统计只包含未完工且非设计阶段的可见项目；退回设计部 / 暂停项目由共享 hook 隐藏。
    let list = visibleProjects.filter((p) => p.stage !== "design" && p.phaseIndex < COMPLETED_INDEX);
    // 同一维度的勾选项取并集，团队、阶段、状态和搜索等不同维度取交集。
    if (teamFilter.length) list = list.filter((p) => teamFilter.includes(p.team));
    // 【需求·分公司筛选去冗余】施工组是分公司的子集（见 TEAM_BRANCH：LA一/二/三组⊂洛杉矶、北加组⊂北加州、SD⊂圣地亚哥），
    // 团队已锁定时再按分公司筛要么恒真（选中该团队所属分公司）、要么矛盾（选别家=0 条）。
    // 看板筛选栏的「分公司」下拉已按需求移除，branchFilter 现在只可能来自「分公司统计卡」或 URL（如 /board?branch=la），
    // 故仅在「全部团队」视角下套用；同时防止残留值把结果误筛成空。
    if (!teamFilter.length && branchFilter !== "all") list = list.filter((p) => effBranch(p) === branchFilter);
    if (salesFilter !== "all") list = list.filter((p) => (p.sales || "") === salesFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      // p.address 可能为空（如 P044 无地址在建项目）—— 直接 toLowerCase 会让看板搜索整体崩掉（错误边界白屏一块），必须兜空串。
      list = list.filter((p) => (p.address || "").toLowerCase().includes(q) || (p.manager || "").toLowerCase().includes(q) || (p.clientName || "").toLowerCase().includes(q));
    }
    let out = list.map((p) => ({ p, timing: phaseTiming(p), risk: assessRisk(p).level }));
    if (riskFilter !== "all") out = out.filter((r) => r.risk === riskFilter);
    if (statusFilter.length) out = out.filter((r) => statusFilter.includes(r.timing?.status || "normal"));
    return out;
  }, [visibleProjects, teamFilter, statusFilter, riskFilter, query, salesFilter, branchFilter]);
  const branchCounts = useMemo(() => {
    const active = visibleProjects.filter((p) => p.stage !== "design" && p.phaseIndex < COMPLETED_INDEX);
    const m = {};
    active.forEach((p) => { const b = effBranch(p); const tm = phaseTiming(p); m[b] = m[b] || { id: b, count: 0, over: 0 }; m[b].count += 1; if (tm?.status === "over") m[b].over += 1; });
    const order = branchList.map((b) => b.id);
    return Object.values(m).sort((a, b) => (order.indexOf(a.id) === -1 ? 99 : order.indexOf(a.id)) - (order.indexOf(b.id) === -1 ? 99 : order.indexOf(b.id)));
  }, [visibleProjects, branchList]);
  // 工程组成员（非总部）：统计卡只显示本人所属小组（可多组）在建 / 超期，不显示全公司与其他分公司数量
  const myTeams = userTeams(user);
  const teamScoped = user.role === "member" && user.region !== "hq" && myTeams.length > 0;
  const teamStats = useMemo(() => {
    if (!teamScoped) return null;
    const stat = (ids) => { const mine = visibleProjects.filter((p) => ids.includes(p.team) && p.stage !== "design" && p.phaseIndex < COMPLETED_INDEX); return { count: mine.length, over: mine.filter((p) => phaseTiming(p)?.status === "over").length }; };
    const per = myTeams.map((id) => ({ id, ...stat([id]), name: TEAMS.find((x) => x.id === id)?.[lang === "zh" ? "zh" : "name"] || id }));
    return { per, all: myTeams.length > 1 ? { id: "all", ...stat(myTeams), name: lang === "zh" ? "全部我的小组" : "All my teams" } : null };
  }, [teamScoped, visibleProjects, myTeams, lang]);
  const [reassign, setReassign] = useState(null);
  const canSeeReassign = ["admin", "ceo", "eng_manager", "design_manager", "hr"].includes(user.role);
  const loadReassign = useCallback(() => {
    if (!canSeeReassign) return;
    api.get("/hrms/reassign-pending").then(({ data }) => setReassign(data)).catch(() => setReassign(null));
  }, [api, canSeeReassign]);
  useEffect(() => { loadReassign(); }, [loadReassign]);
  // 二-6 修复：reloadBoard 原先串联的 loadMissing() 已随「缺少签约归档」横幅删除（函数已不存在，
  // 残留调用会导致整页 ReferenceError 白屏），此处一并移除。
  const reloadBoard = useCallback(async () => { await refresh(); loadReassign(); loadUnread(); }, [refresh, loadReassign, loadUnread]);
  // 【需求·销售选项跟随团队】销售下拉原先取自「全部可见项目」，选定团队后仍会列出别家团队的销售（选中必然筛出 0 条）。
  // 改为跟随所选团队的并集收敛，只列出这些团队项目里实际出现过的销售。
  const salesOptions = useMemo(() => {
    const scoped = !teamFilter.length ? visibleProjects : visibleProjects.filter((p) => teamFilter.includes(p.team));
    return [...new Set(scoped.map((p) => (p.sales || "").trim()).filter((x) => x && x !== "None"))].sort();
  }, [visibleProjects, teamFilter]);
  // 【需求·销售选项跟随团队·配套】切换团队后，若已选销售不在新团队范围内，自动回落「全部销售」，避免筛出空结果。
  useEffect(() => {
    if (salesFilter !== "all" && !salesOptions.includes(salesFilter)) setSalesFilter("all");
  }, [salesOptions]); // eslint-disable-line react-hooks/exhaustive-deps
  // 排序规则（2026-10-13 调整）：主键由「未读优先」改为「阶段进度升序」——
  //   阶段越低（phaseIndex 越小）越靠前显示；同阶段内再按 未读降序 → 超期偏差降序 → 地址字典序。
  //   原规则（未读 → 偏差）会把阶段靠后的项目顶到最前，与「施工进度列表」按进度浏览的直觉相反。
  //   末位用地址做 tie-breaker，保证排序稳定确定（同键时不再依赖数组原始顺序而抖动）。
  const sortedRows = useMemo(() => {
    let out = baseRows;
    if (phaseFilter.length) out = out.filter((r) => phaseFilter.includes(String(r.p.phaseIndex)));
    return [...out].sort((a, b) =>
      (a.p.phaseIndex - b.p.phaseIndex)
      || ((unreadMap[b.p.id]?.unread || 0) - (unreadMap[a.p.id]?.unread || 0))
      || ((b.timing?.deviation ?? -999) - (a.timing?.deviation ?? -999))
      || String(a.p.address || "").localeCompare(String(b.p.address || ""))
    );
  }, [baseRows, phaseFilter, unreadMap]);

  // 点击「完成本阶段」后冻结行序（pinOrder）。
  // 需求背景：推进后 phaseIndex 变化，若按新规则重排，刚操作的行会跳到列表别处，视线与鼠标位置全乱。
  //   故在一次「未切换筛选」的浏览会话内，用首次点击时的行 id 顺序钉住：只更新行内容，不改行位置。
  //   清除时机：任一筛选条件变化（用户主动换视角）时恢复按规则排，见下方重置 effect。
  const [pinOrder, setPinOrder] = useState(null);
  const rows = useMemo(() => {
    if (!pinOrder) return sortedRows;
    const pos = new Map(pinOrder.map((id, i) => [id, i]));
    // 不在 pin 里的新行（如他人新建项目）统一排到最后，已钉住的相对顺序保持不变
    return [...sortedRows].sort((a, b) => {
      const ia = pos.has(a.p.id) ? pos.get(a.p.id) : Number.MAX_SAFE_INTEGER;
      const ib = pos.has(b.p.id) ? pos.get(b.p.id) : Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });
  }, [sortedRows, pinOrder]);
  // 市检是小组的实际预约，不能因下方列表按阶段/超期/关键词过滤而被悄悄隐藏。
  // 只跟随工程可见项目、施工小组与分公司范围；写权限仍由市检服务端逐项目校验。
  const calendarProjects = useMemo(() => visibleProjects.filter((p) =>
    (!teamFilter.length || teamFilter.includes(p.team)) &&
    (teamFilter.length || branchFilter === "all" || effBranch(p) === branchFilter)), [visibleProjects, teamFilter, branchFilter]);
  // 列表分批渲染：首屏 20 行，滚到底部附近再渲染下 20 行（数据已在本地，只减少首屏渲染量；与已签约客户列表一致）
  // 重置时机（2026-10-13 调整）：仅在「筛选条件变化」时压回首屏并解除行序冻结。
  //   原实现依赖 [rows]，导致每点一次「完成本阶段」（rows 变化）就把已展开的行数压回 20，列表视觉回跳。
  const [shownN, setShownN] = useState(LIST_PAGE);
  useEffect(() => {
    setShownN(LIST_PAGE);
    setPinOrder(null);
  }, [phaseFilter, statusFilter, riskFilter, teamFilter, query, salesFilter, branchFilter]);
  const shownRows = useMemo(() => rows.slice(0, shownN), [rows, shownN]);
  const hasMoreRows = shownN < rows.length;
  const listSentinel = useRef(null);
  const showMore = useCallback(() => setShownN((n) => Math.min(n + LIST_PAGE, rows.length)), [rows.length]);
  // 「完成本阶段 / 竣工」共用点击：先钉住当前行序（本轮浏览内不再重排），再调推进接口。
  const handleAdvance = useCallback(async (id) => {
    if (!beginProjectAction(id, "advance")) return;
    setPinOrder((prev) => prev ?? rows.map((r) => r.p.id));
    try { await advancePhase(id); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message || (lang === "zh" ? "阶段推进失败，请重试" : "Could not advance the phase. Please retry.")); }
    finally { finishProjectAction(id); }
  }, [rows, advancePhase, lang, beginProjectAction, finishProjectAction]);
  useEffect(() => {
    const el = listSentinel.current;
    if (!el || !hasMoreRows || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) showMore(); }, { rootMargin: `${LIST_PREFETCH_PX}px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMoreRows, showMore, shownN]);

  const teamOptions = TEAMS.map((team) => ({ value: team.id, label: `${team[lang === "zh" ? "zh" : "name"]}${!canManageAll && inUserTeam(user, team.id) ? (lang === "zh" ? "（本团队）" : " (mine)") : ""}` }));
  const phaseOptions = PHASES.map((phase, index) => ({ value: String(index), label: `${index + 1}-${phase[lang === "zh" ? "zh" : "en"]}` }));
  const statusOptions = Object.entries(STATUS_META).map(([value, status]) => ({ value, label: status[lang === "zh" ? "zh" : "en"] }));
  const selectionLabel = (values, options) => options.filter((option) => values.includes(option.value)).map((option) => option.label).join("、");
  const teamLabel = isSales && !teamFilter.length
    ? (lang === "zh" ? "我的客户项目（只读）" : "My clients' projects (read-only)")
    : isAftersales && !teamFilter.length
      ? (lang === "zh" ? "本分公司全部在建项目（只读）" : "All branch projects (read-only)")
      : !teamFilter.length
      ? (lang === "zh" ? "全部工程项目" : "All Team Projects")
      : teamFilter.map((id) => `${TEAMS.find((team) => team.id === id)?.[lang === "zh" ? "zh" : "name"] || id}${!canManageAll && !inUserTeam(user, id) ? (lang === "zh" ? "（只读）" : " (read-only)") : ""}`).join("、");

  const phaseCounts = useMemo(
    () => PHASES.map((ph, i) => ({ key: ph.key, idx: i, zh: ph.zh, en: ph.en, color: ph.color, count: baseRows.filter((r) => r.p.phaseIndex === i).length })),
    [baseRows]
  );

  const exportExcel = () => {
    const data = rows.map(({ p, timing }) => {
      const phase = getPhase(p.phaseIndex);
      const sm = STATUS_META[timing?.status || "normal"];
      return {
        [lang === "zh" ? "编号" : "ID"]: p.id,
        [lang === "zh" ? "团队" : "Team"]: p.teamName,
        [lang === "zh" ? "分公司" : "Branch"]: branchLabel(effBranch(p)),
        [lang === "zh" ? "项目地址" : "Address"]: p.address,
        [lang === "zh" ? "负责人" : "Manager"]: p.manager,
        [lang === "zh" ? "当前阶段" : "Current Phase"]: `${p.phaseIndex + 1}-${lang === "zh" ? phase.zh : phase.en}`,
        [lang === "zh" ? "阶段开始" : "Phase Start"]: timing ? fmtDate(timing.phaseStart) : "",
        [lang === "zh" ? "停留天数" : "Days In Phase"]: timing?.stayed ?? 0,
        [lang === "zh" ? "标准工期" : "Standard"]: timing?.standard ?? "",
        [lang === "zh" ? "偏差" : "Deviation"]: timing?.deviation ?? 0,
        [lang === "zh" ? "状态" : "Status"]: lang === "zh" ? sm.zh : sm.en,
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, lang === "zh" ? "工程进度" : "Progress");
    XLSX.writeFile(wb, `engineering_progress_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // 原独立栏重复占用首屏；同一组控件在桌面 portal、手机正文之间迁移，避免重复 testid 和操作入口。
  // 摘要只压缩团队名称，完整内容通过悬停或键盘焦点提示可读；数量、只读标识和权限条件继续保留。
  const boardToolbar = (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 md:max-w-[70vw] md:justify-end lg:max-w-[55vw]" data-testid="board-sticky-header">
      <p className="flex min-w-0 flex-wrap items-center gap-x-1 text-xs text-slate-500">
        <TooltipProvider delayDuration={200}><InfoTooltip><TooltipTrigger asChild>
          <span className="max-w-40 truncate rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 xl:max-w-52" tabIndex={0}>{teamLabel}</span>
        </TooltipTrigger><TooltipContent className="max-w-xs break-words">{teamLabel}</TooltipContent></InfoTooltip></TooltipProvider>
        <span className="whitespace-nowrap tabular-nums">· {lang === "zh" ? `共 ${rows.length} 个项目` : `${rows.length} projects`}</span>
        {isSales && totalUnread > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white" data-testid="construction-total-unread">{lang === "zh" ? `${totalUnread} 条未读` : `${totalUnread} unread`}</span>}
      </p>
      {isSales && <span className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700" data-testid="board-scope-mine"><HardHat aria-hidden="true" className="h-3.5 w-3.5" />{lang === "zh" ? "我的客户" : "My clients"}</span>}
      <div className="flex flex-wrap items-center gap-2">
        <BreadcrumbPopButton
          label={lang === "zh" ? "工程总览" : "Board Overview"}
          icon={LayoutDashboard}
          testid="bc-board-btn"
          title={lang === "zh" ? "工程总览" : "Board Overview"}
          subtitle={lang === "zh" ? "开工倒计时 · AI 部门晨报 · 各项目阶段分布" : "Kickoff · AI brief · phases"}
          popClassName="w-[1400px] max-w-[calc(100vw-2.5rem)] max-h-[82vh] overflow-y-auto"
          autoOpen={false}
        >
          <div className="space-y-5">
            <KickoffCountdownCards />
            <DeptBriefCard compact />
            <div className="bg-white rounded-xl border border-slate-200 p-5 cursor-pointer" data-testid="teamboard-phase-chart" onClick={() => setPhaseFilter("all")}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-slate-900">{lang === "zh" ? "各项目阶段分布" : "Projects by Phase"}</h3>
                {phaseFilter.length > 0 && (
                  <button data-testid="phase-chart-clear" onClick={(e) => { e.stopPropagation(); setPhaseFilter("all"); }} className="text-xs font-medium text-red-600 hover:underline">
                    {lang === "zh" ? "✕ 显示全部阶段" : "✕ Show all phases"}
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400 mb-4">{lang === "zh" ? "点击柱状图可多选阶段；再次点击取消，点击空白处显示全部" : "Click bars to select phases; click again to deselect, or blank area to show all"}</p>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={phaseCounts} margin={{ top: 18, right: 8, left: -18, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey={lang === "zh" ? "zh" : "en"} tick={{ fontSize: 11, fill: "#64748b" }} angle={-25} textAnchor="end" interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} cursor="pointer"
                    onClick={(_d, index, e) => { e?.stopPropagation?.(); setPhaseFilter((prev) => prev.includes(String(index)) ? prev.filter((value) => value !== String(index)) : [...prev, String(index)]); }}>
                    {phaseCounts.map((p, i) => <Cell key={i} fill={p.color} fillOpacity={!phaseFilter.length || phaseFilter.includes(String(p.idx)) ? 1 : 0.28} />)}
                    <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 700, fill: "#334155" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </BreadcrumbPopButton>
        <Button type="button" variant="outline" size="sm" data-testid="export-excel-btn" onClick={exportExcel} className="h-11 rounded-lg px-3 text-xs shadow-none md:h-8">
          <FileDown aria-hidden="true" className="h-3.5 w-3.5" />{lang === "zh" ? "导出Excel" : "Export Excel"}
        </Button>
        {canCreateProject && <Button type="button" size="sm" onClick={() => setShowCreate(true)} className="h-11 rounded-lg px-3 text-xs shadow-none md:h-8">
          <Plus aria-hidden="true" className="h-3.5 w-3.5" />{t("createProject")}
        </Button>}
      </div>
    </div>
  );

  return (
    <PullToRefresh onRefresh={reloadBoard} zh={lang === "zh"}>
    <div className="-mt-2 flex min-w-0 flex-col gap-3 sm:-mt-3 lg:-mt-4">
      {/* 桌面复用面包屑吸顶层；手机和插槽尚未就绪时原位渲染，始终只有一套工具栏。 */}
      {bcSlot && !mobile ? createPortal(boardToolbar, bcSlot) : boardToolbar}
      <h1 className="sr-only">{isSales ? (lang === "zh" ? "我的客户施工进度" : "My Clients' Projects") : isAftersales ? (lang === "zh" ? "本分公司在建项目（只读）" : "Branch Projects (read-only)") : t("teamBoard")}</h1>
      {teamScoped && myTeams.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tb-team-chips" data-active={teamFilter.length ? teamFilter.join(",") : "all"}>
          {[{ id: "all", label: lang === "zh" ? "全部我的小组" : "All my teams" }, ...myTeams.map((id) => ({ id, label: TEAMS.find((x) => x.id === id)?.[lang === "zh" ? "zh" : "name"] || id }))].map((c) => {
            const on = c.id === "all" ? !teamFilter.length : teamFilter.includes(c.id);
            return (
              <button key={c.id} type="button" onClick={() => setTeamFilter(c.id)} data-testid={c.id === "all" ? "tb-team-chip-all" : `tb-team-chip-${c.id}`} data-on={on ? 1 : 0}
                className={`text-xs rounded-full border px-3 py-1 transition-colors ${on ? "bg-brand-grad text-white border-transparent shadow-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand-300"}`}>{c.label}</button>
            );
          })}
        </div>
      )}

      {teamScoped && teamStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="team-stats" data-teams={myTeams.length}>
          {[...(teamStats.all ? [teamStats.all] : []), ...teamStats.per].map((ts) => {
            const active = ts.id === "all" ? !teamFilter.length : teamFilter.includes(ts.id);
            return (
              <button key={ts.id} data-testid={ts.id === "all" ? "team-stat-all" : `team-stat-${ts.id}`} data-count={ts.count} data-over={ts.over} onClick={() => setTeamFilter(ts.id)}
                className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${active ? "bg-brand-grad text-white border-transparent shadow-brand" : "bg-white border-slate-200 hover:border-brand-300"}`}>
                <p className={`text-[11px] truncate ${active ? "text-white/80" : "text-slate-400"}`}>{ts.name}{ts.id === "all" ? "" : (lang === "zh" ? "（本团队）" : " (my team)")}</p>
                <p className="text-xl font-black leading-tight">{ts.count}<span className="text-xs font-normal ml-1">{lang === "zh" ? "在建" : "active"}</span>
                  {ts.over > 0 && <span className={`ml-2 text-[11px] font-semibold ${active ? "text-white" : "text-red-600"}`}>{lang === "zh" ? `超期 ${ts.over}` : `${ts.over} overdue`}</span>}</p>
              </button>
            );
          })}
        </div>
      )}
      {/* 【需求·层级去重·配套】分公司统计卡同样是「分公司」维度：选定具体团队后整条分公司维度统一收起，
          避免出现"卡片能点高亮、但列表已不受分公司条件影响"的名实不符。切回「全部团队」后恢复显示。 */}
      {!teamScoped && !teamFilter.length && branchCounts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="branch-stats">
          <button data-testid="branch-stat-all" onClick={() => setBranchFilter("all")}
            className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${branchFilter === "all" ? "bg-brand-grad text-white border-transparent shadow-brand" : "bg-white border-slate-200 hover:border-brand-300"}`}>
            <p className={`text-[11px] ${branchFilter === "all" ? "text-white/80" : "text-slate-400"}`}>{lang === "zh" ? "全部分公司" : "All branches"}</p>
            <p className="text-xl font-black leading-tight">{branchCounts.reduce((s, b) => s + b.count, 0)}<span className="text-xs font-normal ml-1">{lang === "zh" ? "在建" : "active"}</span></p>
          </button>
          {branchCounts.map((b) => (
            <button key={b.id || "none"} data-testid={`branch-stat-${b.id || "none"}`} data-count={b.count} onClick={() => setBranchFilter(branchFilter === b.id ? "all" : b.id)}
              className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${branchFilter === b.id ? "bg-brand-grad text-white border-transparent shadow-brand" : "bg-white border-slate-200 hover:border-brand-300"}`}>
              <p className={`text-[11px] truncate flex items-center justify-between ${branchFilter === b.id ? "text-white/80" : "text-slate-400"}`}>{branchLabel(b.id)}
                {b.id && <span role="link" data-testid={`branch-report-link-${b.id}`} onClick={(e) => { e.stopPropagation(); nav(`/branch-report/${b.id}`); }} className={`text-[10px] font-semibold hover:underline ${branchFilter === b.id ? "text-white" : "text-brand-600"}`}>{lang === "zh" ? "周报 →" : "Report →"}</span>}</p>
              <p className="text-xl font-black leading-tight">{b.count}<span className="text-xs font-normal ml-1">{lang === "zh" ? "在建" : "active"}</span>
                {b.over > 0 && <span className={`ml-2 text-[11px] font-semibold ${branchFilter === b.id ? "text-white" : "text-red-600"}`}>{lang === "zh" ? `超期 ${b.over}` : `${b.over} overdue`}</span>}</p>
            </button>
          ))}
        </div>
      )}
      {canManageAll && ["admin", "eng_manager", "ceo"].includes(user.role) && (
        <div className="flex flex-wrap items-center gap-2 -mt-1" data-testid="branch-tools">
          {missingBranch > 0 && !branchGaps && (
            <button data-testid="branch-backfill-btn" onClick={backfillBranch}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-3 py-1 transition-colors">
              <Building2 className="h-3.5 w-3.5" />{lang === "zh" ? `${missingBranch} 个项目未设所属分公司 · 一键按施工组补齐` : `${missingBranch} project(s) without branch · backfill from crew`}
            </button>
          )}
          {branchGaps && <span className="text-xs text-emerald-700" data-testid="branch-backfill-result">{lang === "zh" ? `已补齐 ${branchGaps.updated} 个项目` : `Backfilled ${branchGaps.updated}`}</span>}
          {/* 二-4：以下运维按钮已删除。老板原话（03:56-04:13）：这些功能（立即推送分公司周报 / 催办统计 /
              导入历史项目 Excel / QuickBooks 同步 / 标准工期设置）目前工作中都没有使用过，能删就删掉。
              保留分公司统计卡与「一键按施工组补齐」（数据修复仍有价值）。 */}
        </div>
      )}

      {canSeeReassign && reassign?.total > 0 && (
        <ReassignBanner data={reassign} lang={lang} highlight={!!sp.get("reassign")} onRefresh={loadReassign} onOpen={(id) => goProject(id)}
          onChanged={() => { refresh(); setTimeout(loadReassign, 1500); }} />
      )}

      {/* 二-6：原「缺少签约归档」提醒横幅及补档回执已删除。
          老板原话（07:34-08:21）：88 个项目缺少签约补档的提醒和「合同签署跟进」重复，
          没有合同的项目根本没办法补档；需要补档时财务会直接联系，旧项目也没法补。
          所以这个提醒没必要，直接删掉。 */}

      {/* 切组只改变本地展示范围：同周始终读取服务端已授权的全部预约，避免单选/多选来回切换
          重复请求、清空周历并把下方项目列表推来推去。calendarProjects 同时限定预约、待预约、
          待核对、会面与登记选项；服务端权限校验及保存后的刷新机制保持不变。
          日历改为紧凑工具栏后，桌面筛选同步缩为 32px；手机仍保留 44px 点击高度。 */}
      <WeeklyInspectionCalendar onSelect={(p) => goProject(p.id)} projects={calendarProjects}
        scopeControl={<div className="flex min-h-11 items-center gap-2 text-xs text-slate-600 sm:min-h-8">{lang === "zh" ? "施工小组" : "Crew"}
          <BoardMultiSelect values={teamFilter} onChange={setTeamFilter} options={teamOptions}
            label={lang === "zh" ? "施工小组" : "Crews"} allLabel={lang === "zh" ? "全部小组" : "All crews"}
            testid="inspection-team-filter" className="h-11 w-32 rounded-lg text-xs shadow-none sm:h-8" />
        </div>} />

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="project-table">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="teamboard-filter-bar">
          {/* 按页面精简需求移除列表标题，将搜索框放到原标题位置；团队、阶段、状态支持连续多选。 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("searchProjects")} aria-label={t("searchProjects")} className="pl-9 w-48 h-9" />
          </div>
          <BoardMultiSelect values={teamFilter} onChange={setTeamFilter} options={teamOptions}
            label={lang === "zh" ? "团队" : "Teams"} allLabel={lang === "zh" ? "全部团队" : "All Teams"} testid="team-filter" />
          <BoardMultiSelect values={phaseFilter} onChange={setPhaseFilter} options={phaseOptions}
            label={lang === "zh" ? "阶段" : "Phases"} allLabel={lang === "zh" ? "全部阶段" : "All Phases"} testid="phase-filter" />
          <BoardMultiSelect values={statusFilter} onChange={(values) => { setStatusFilter(values); if (sp.has("status")) clearUrlFilter("status"); }} options={statusOptions}
            label={lang === "zh" ? "状态" : "Status"} allLabel={lang === "zh" ? "全部状态" : "All Status"} testid="status-filter" className="h-9 w-28" />
          {teamFilter.length > 0 && <FilterChip testid="chip-team" label={`${lang === "zh" ? "团队" : "Team"}: ${selectionLabel(teamFilter, teamOptions)}`} onClear={() => setTeamFilter("all")} />}
          {phaseFilter.length > 0 && <FilterChip testid="chip-phase" label={`${lang === "zh" ? "阶段" : "Phase"}: ${selectionLabel(phaseFilter, phaseOptions)}`} onClear={() => setPhaseFilter("all")} />}
          {statusFilter.length > 0 && <FilterChip testid="chip-status" label={`${lang === "zh" ? "状态" : "Status"}: ${selectionLabel(statusFilter, statusOptions)}`} onClear={() => { setStatusFilter("all"); clearUrlFilter("status"); }} />}
          {riskFilter !== "all" && <FilterChip testid="chip-risk" label={`${lang === "zh" ? "AI 风险" : "AI Risk"}: ${riskFilter === "high" ? (lang === "zh" ? "高风险" : "High") : (lang === "zh" ? "正常" : "On Track")}`} onClear={() => { setRiskFilter("all"); clearUrlFilter("risk"); }} />}
          {query.trim() && <FilterChip testid="chip-query" label={`${lang === "zh" ? "搜索" : "Search"}: ${query}`} onClear={() => setQuery("")} />}
          {salesFilter !== "all" && <FilterChip testid="chip-sales" label={`${lang === "zh" ? "销售" : "Sales"}: ${salesFilter}`} onClear={() => setSalesFilter("all")} />}
          {!teamFilter.length && branchFilter !== "all" && <FilterChip testid="chip-branch" label={`${lang === "zh" ? "分公司" : "Branch"}: ${branchLabel(branchFilter)}`} onClear={() => setBranchFilter("all")} />}
          {/* 按页面精简需求删除业主语言批量设置及其请求逻辑，让工具栏只保留筛选；项目已有语言数据不变。 */}
          {(phaseFilter.length > 0 || statusFilter.length > 0 || riskFilter !== "all" || teamFilter.length > 0 || query.trim() || salesFilter !== "all" || branchFilter !== "all")
            ? <button data-testid="clear-all-filters" onClick={() => { setPhaseFilter("all"); setStatusFilter("all"); setRiskFilter("all"); setTeamFilter("all"); setQuery(""); setSalesFilter("all"); setBranchFilter("all"); const next = new URLSearchParams(sp); next.delete("status"); next.delete("risk"); setSp(next, { replace: true }); }} className="text-xs text-slate-500 hover:text-red-600 hover:underline">{lang === "zh" ? "清除全部" : "Clear all"}</button>
            : <span className="text-xs text-slate-400">{lang === "zh" ? "未设置筛选" : "No filters"}</span>}
          <span className="text-xs text-slate-400 ml-auto">{rows.length} {lang === "zh" ? "个项目" : "projects"}</span>
        </div>
        {/* 手机用纵向项目卡避免九列表格横滑；复用同一批 shownRows 和下方加载入口，
            详情返回定位仍依赖 data-board-row，权限按钮继续由原 canOperateRow 判断。 */}
        {mobile ? <div className="space-y-3 bg-slate-50/60 p-3" data-testid="project-mobile-list">
          {shownRows.map(({ p, timing }) => <MobileProjectCard key={p.id} p={p} timing={timing} phase={getPhase(p.phaseIndex)} status={STATUS_META[timing?.status || "normal"]}
            teamName={TEAMS.find(t => t.id === p.team)?.[lang === "zh" ? "zh" : "name"] || p.teamName || p.team}
            zh={lang === "zh"} unread={unreadMap[p.id]?.unread} survey={surveyOverdue[p.id]} onOpen={openProject} pendingAction={pendingActionById[p.id]}
            onAdvance={canOperateRow(p) && p.phaseIndex <= 6 ? handleAdvance : undefined}
            onUrge={canManageAll && timing?.status === "over" ? urge : undefined} urged={urged.includes(p.id)}>
            <StartCountdownChip p={p} lang={lang} />
            {isSales && unreadMap[p.id]?.noteCount > 0 && <p className="text-xs text-slate-600">{lang === "zh" ? "施工留言" : "Project notes"} · {unreadMap[p.id].noteCount}</p>}
            {siteCosts[p.id] && <><SiteCostChip c={siteCosts[p.id]} lang={lang} open={costOpen === p.id} onToggle={() => setCostOpen(costOpen === p.id ? null : p.id)} />
              {costOpen === p.id && <div className="max-w-full overflow-x-auto"><table className="w-full"><tbody><SiteCostDetailRow c={siteCosts[p.id]} lang={lang} colSpan={1} /></tbody></table></div>}</>}
          </MobileProjectCard>)}
          {!rows.length && <p className="py-8 text-center text-sm text-slate-500">{lang === "zh" ? "没有符合条件的项目" : "No matching projects"}</p>}
        </div> : <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "项目地址" : "Address"}</th>
                <th className="text-left font-medium px-3 py-3">{lang === "zh" ? "当前阶段" : "Current Phase"}</th>
                <th className="text-left font-medium px-3 py-3">{lang === "zh" ? "阶段开始" : "Phase Start"}</th>
                <th className="text-left font-medium px-3 py-3">{lang === "zh" ? "停留天数" : "Days In Phase"}</th>
                <th className="text-right font-medium px-3 py-3">{lang === "zh" ? "标准工期" : "Standard"}</th>
                <th className="text-right font-medium px-3 py-3">{lang === "zh" ? "偏差" : "Deviation"}</th>
                <th className="text-center font-medium px-3 py-3">{lang === "zh" ? "状态" : "Status"}</th>
                <th className="text-left font-medium px-3 py-3">{lang === "zh" ? "阶段进度" : "Progress"}</th>
                <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "操作" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-300 py-10">—</td></tr>}
              {shownRows.map(({ p, timing }) => {
                const phase = getPhase(p.phaseIndex);
                const sm = STATUS_META[timing?.status || "normal"];
                const barPct = timing ? Math.min(100, Math.round((timing.stayed / (timing.standard * 2)) * 100)) : 0;
                const dev = timing?.deviation ?? 0;
                const pendingAction = pendingActionById[p.id];
                return (
                  <React.Fragment key={p.id}>
                  <tr data-testid={`project-row-${p.id}`} data-board-row={p.id} data-owner-lang={p.ownerLang || "zh"} data-unread={unreadMap[p.id]?.unread || 0} onClick={() => openProject(p)} className={`border-t border-slate-100 cursor-pointer ${unreadMap[p.id]?.unread > 0 ? "bg-red-50/40 hover:bg-red-50" : timing?.status === "over" ? "bg-red-50/70 hover:bg-red-50" : "hover:bg-slate-50/60"}`}>
                    <td className="px-4 py-3"><p className="font-medium text-slate-800 max-w-[240px] truncate flex items-center gap-1.5"><span className="truncate">{p.address}</span>{unreadMap[p.id]?.unread > 0 && <span data-testid={`construction-unread-${p.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-white bg-red-600 rounded-full px-1.5 py-0.5 shrink-0">{lang === "zh" ? `${unreadMap[p.id].unread} 未读` : `${unreadMap[p.id].unread} new`}</span>}{/* 二-6：原「缺归档」行内徽标（missingIds）已随提醒横幅一并删除 */}{surveyOverdue[p.id] && <span data-testid={`survey-risk-overdue-badge-${p.id}`} data-escalated={surveyOverdue[p.id].escalated ? "1" : "0"} title={surveyOverdue[p.id].tasks.map((t) => `${t.title}（${lang === "zh" ? `超期 ${t.overdueDays} 天` : `${t.overdueDays}d overdue`}）`).join("\n")} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 shrink-0 border ${surveyOverdue[p.id].escalated ? "text-white bg-red-600 border-red-700" : "text-red-700 bg-red-100 border-red-300"}`}><AlertTriangle className="h-3 w-3" />{lang === "zh" ? `踏勘风险超期 ${surveyOverdue[p.id].maxDays} 天` : `Survey risk overdue ${surveyOverdue[p.id].maxDays}d`}</span>}</p><span className="text-[11px] text-slate-400 flex items-center gap-1.5 flex-wrap">{p.clientName || ""}{isSales && unreadMap[p.id]?.noteCount > 0 && <span className="inline-flex items-center gap-0.5" data-testid={`construction-notes-${p.id}`}><MessageSquare className="h-3 w-3" />{unreadMap[p.id].noteCount}</span>}<StartCountdownChip p={p} lang={lang} />{siteCosts[p.id] && <SiteCostChip c={siteCosts[p.id]} lang={lang} open={costOpen === p.id} onToggle={(e) => { e.stopPropagation(); setCostOpen(costOpen === p.id ? null : p.id); }} />}</span></td>
                    <td className="px-3 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: phase.color }} /><span className="text-slate-700">{p.phaseIndex + 1}-{lang === "zh" ? phase.zh : phase.en}</span></span></td>
                    <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{timing ? fmtDate(timing.phaseStart) : "—"}</td>
                    <td className="px-3 py-3 w-32">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${barPct}%`, background: sm.color }} /></div>
                        <span className="text-xs text-slate-600 w-10 text-right">{timing?.stayed ?? 0}{lang === "zh" ? "天" : "d"}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right text-slate-500">{timing?.standard ?? "—"}{timing ? (lang === "zh" ? "天" : "d") : ""}</td>
                    <td className="px-3 py-3 text-right font-semibold" style={{ color: dev > 0 ? "#e11b22" : "#16a34a" }}>{dev > 0 ? `+${dev}` : dev}</td>
                    <td className="px-3 py-3 text-center"><span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: sm.bg, color: sm.color }}>{lang === "zh" ? sm.zh : sm.en}</span></td>
                    <td className="px-3 py-3"><StepProgress current={p.phaseIndex} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {canManageAll && timing?.status === "over" && (
                          <button type="button" data-testid={`urge-btn-${p.id}`} onClick={() => urge(p, timing)} disabled={!!pendingAction || urged.includes(p.id)} aria-busy={pendingAction === "urge" ? "true" : undefined} className="inline-flex items-center gap-1 text-xs text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 rounded-md px-2 py-1 transition-colors">{pendingAction === "urge" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Bell className="h-3.5 w-3.5" aria-hidden="true" />}{urged.includes(p.id) ? (lang === "zh" ? "已催办" : "Urged") : pendingAction === "urge" ? (lang === "zh" ? "发送中…" : "Sending…") : (lang === "zh" ? "催办" : "Urge")}</button>
                        )}
                        {canOperateRow(p) && p.phaseIndex < 6 && (
                          <button type="button" data-testid={`advance-btn-${p.id}`} onClick={() => handleAdvance(p.id)} disabled={!!pendingAction} aria-busy={pendingAction === "advance" ? "true" : undefined} className="inline-flex items-center gap-1 text-xs text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-md px-2 py-1 transition-colors">{pendingAction === "advance" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />}{pendingAction === "advance" ? (lang === "zh" ? "推进中…" : "Advancing…") : (lang === "zh" ? "完成本阶段" : "Advance")}</button>
                        )}
                        {canOperateRow(p) && p.phaseIndex === 6 && (
                          <button type="button" data-testid={`complete-btn-${p.id}`} onClick={() => handleAdvance(p.id)} disabled={!!pendingAction} aria-busy={pendingAction === "advance" ? "true" : undefined} className="inline-flex items-center gap-1 text-xs text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 disabled:cursor-not-allowed rounded-md px-2 py-1 transition-colors">{pendingAction === "advance" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />}{pendingAction === "advance" ? (lang === "zh" ? "推进中…" : "Advancing…") : (lang === "zh" ? "竣工" : "Complete")}</button>
                        )}
                        <button data-testid={`edit-btn-${p.id}`} onClick={() => openProject(p)} className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 hover:border-brand-300 hover:text-brand-500 rounded-md px-2 py-1 transition-colors"><Pencil className="h-3.5 w-3.5" />{lang === "zh" ? "详情" : "Detail"}</button>
                      </div>
                    </td>
                  </tr>
                  {costOpen === p.id && siteCosts[p.id] && <SiteCostDetailRow c={siteCosts[p.id]} lang={lang} colSpan={9} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>}
        {rows.length > 0 && (
          <div ref={listSentinel} className="flex items-center justify-center gap-3 px-4 py-3 border-t border-slate-100 text-xs text-slate-400" data-testid="tb-list-footer" data-shown={shownRows.length} data-total={rows.length}>
            <span>{lang === "zh" ? `已显示 ${shownRows.length} / ${rows.length}` : `Showing ${shownRows.length} / ${rows.length}`}</span>
            {hasMoreRows
              ? <button type="button" data-testid="tb-load-more-btn" onClick={showMore} className="text-brand-600 hover:underline font-semibold">{lang === "zh" ? "加载更多" : "Load more"}</button>
              : <span data-testid="tb-list-end">{lang === "zh" ? "已全部显示" : "All shown"}</span>}
          </div>
        )}
      </div>

      <CreateProject open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
    </PullToRefresh>
  );
}
